import {
  designSketchSchema,
  type DesignSketch,
  type SaveDesignSketchInput,
} from '@tailoring-crm/shared';
import type { ApiClient } from '../api/client.js';
import { isApiRequestError } from '../api/errors.js';

/**
 * A card's drawing, over the wire.
 *
 * Every call is addressed by the CARD, never by a board: a scene belongs to one card and the card
 * is already reachable by its own id, so there is nothing here that needs to know whether the
 * board hangs on a customer or an order.
 */

/**
 * The scene, or null when this card has never been drawn on.
 *
 * The server answers 404 for "no drawing", and turning that into `null` here is what lets the
 * canvas open BLANK on a card that has none instead of the caller having to decide whether a
 * not-found means "no drawing yet" or "something is wrong". Every other failure still throws —
 * a 403 or a dropped connection must not quietly present itself as an empty sheet, or the first
 * save would overwrite a drawing that was merely unreachable.
 */
export async function getDesignSketch(
  client: ApiClient,
  cardId: string,
): Promise<DesignSketch | null> {
  try {
    return await client.get(`/design-cards/${cardId}/sketch`, { schema: designSketchSchema });
  } catch (error) {
    if (isApiRequestError(error) && error.status === 404) return null;
    throw error;
  }
}

/** Save the drawing. PUT, because a canvas replaces what was there rather than adding to it. */
export async function saveDesignSketch(
  client: ApiClient,
  cardId: string,
  input: SaveDesignSketchInput,
): Promise<DesignSketch> {
  return await client.put(`/design-cards/${cardId}/sketch`, input, { schema: designSketchSchema });
}

export async function deleteDesignSketch(client: ApiClient, cardId: string): Promise<void> {
  await client.del(`/design-cards/${cardId}/sketch`);
}
